/**
 * "Has this user been away long enough to warn an offer-maker?"
 *
 * Why (2026-10-06): people were sending offers to members who hadn't opened
 * PedalxChange in weeks or months, then getting upset when nobody answered.
 * The offer pages now show a heads-up when the other person has been away
 * a while.
 *
 * Only the WEEK of last activity is public: captureUserIP writes the exact
 * time to the private users/{uid} doc and a week id ("2026-W41") that
 * functions/publicProfiles.js mirrors to publicProfiles/{uid}, the doc
 * other users can read. So the rule is stated on the week:
 *
 *   warn when INACTIVE_DAYS have passed since the END of their last
 *   active week.
 *
 * That never warns early: someone active on the last day of that week has
 * been away at least INACTIVE_DAYS. It warns at most 7 days late, so with
 * INACTIVE_DAYS = 30 the notice appears between day 30 and day 37 of real
 * inactivity (the user asked for "30-40 days, never sooner than 30").
 * No week recorded (legacy accounts) means no warning, never a guess.
 */
export const INACTIVE_DAYS = 30;

const DAY_MS = 86400000;

// Monday 00:00 UTC of ISO week `week` of ISO year `year`.
function isoWeekMonday(year, week) {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  const week1Monday = jan4.getTime() - (jan4Day - 1) * DAY_MS;
  return new Date(week1Monday + (week - 1) * 7 * DAY_MS);
}

/**
 * Start (Monday, UTC) of the week a `lastActiveWeek` id refers to, or null
 * when the id is missing or malformed.
 *
 * The id's YEAR is the CALENDAR year of the write (captureUserIP uses
 * getFullYear()), but its week number is the ISO week, so around New Year
 * they disagree: Dec 30, 2024 is written "2024-W01" (ISO week 1 of 2025),
 * and Jan 1, 2027 is written "2027-W53" (ISO week 53 of 2026). A naive read
 * would date the first a year too early, and wrongly flag an active member.
 * So we take every ISO week with that number that contains a day of the
 * labelled calendar year and isn't in the future, and pick the LATEST,
 * which is the generous reading.
 */
export function lastActiveWeekStart(weekId, now = new Date()) {
  const m = /^(\d{4})-W(\d{1,2})$/.exec(typeof weekId === 'string' ? weekId.trim() : '');
  if (!m) return null;
  const labelYear = Number(m[1]);
  const week = Number(m[2]);
  if (week < 1 || week > 53) return null;
  let best = null;
  for (const isoYear of [labelYear - 1, labelYear, labelYear + 1]) {
    const monday = isoWeekMonday(isoYear, week);
    // ISO week 53 only exists in some years: it must still be week 53.
    const thursday = new Date(monday.getTime() + 3 * DAY_MS);
    if (thursday.getUTCFullYear() !== isoYear) continue;
    const sunday = new Date(monday.getTime() + 6 * DAY_MS);
    const touchesLabelYear = monday.getUTCFullYear() === labelYear || sunday.getUTCFullYear() === labelYear;
    if (!touchesLabelYear) continue;
    if (monday.getTime() > now.getTime()) continue;
    if (!best || monday > best) best = monday;
  }
  return best;
}

/** True when the user's last active week ended at least INACTIVE_DAYS ago. */
export function isLongInactive(lastActiveWeek, now = new Date(), days = INACTIVE_DAYS) {
  const start = lastActiveWeekStart(lastActiveWeek, now);
  if (!start) return false;
  const weekEnd = start.getTime() + 7 * DAY_MS;
  return now.getTime() - weekEnd >= days * DAY_MS;
}
